import React, { ComponentType, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DynamicInputComponents, DynamicInputProps } from "./DynamicInput.model";
import { Field, ComplexInputValues } from "@companion-core/shared/app/Interfaces/wizard";
import { getCurrency } from "@companion-core/shared/app/Utils/config";
import { GooglePlaceAddress } from "@companion-core/shared/app/Interfaces/adresses";
import { useDynamicFormContext } from "@companion-core/shared/app/Components/DynamicForm/DynamicFormContext";

export const DynamicInput = ({
  field,
  registerValidation,
  onFieldValidationChange,
}: DynamicInputProps) => {
  const { t } = useTranslation();
  const { toNextStep } = useDynamicFormContext();

  const Component = useMemo<ComponentType<Record<string, unknown>> | null>(() => {
    if (field.type in DynamicInputComponents) {
      return DynamicInputComponents[field.type] as ComponentType<Record<string, unknown>>;
    }
    return null;
  }, [field.type]);

  const getTransformField: Field = useMemo(() => {
    const newField = { ...field }; // create a copy of field
    newField.label = t(newField.label ?? "");
    if (newField.inputAccessibilityLabel)
      newField.inputAccessibilityLabel = t(newField.inputAccessibilityLabel);
    if (newField.confirmationInputAccessibilityLabel)
      newField.confirmationInputAccessibilityLabel = t(
        newField.confirmationInputAccessibilityLabel
      );
    newField.value = newField.value ?? null;
    if (newField.placeholder) newField.placeholder = t(newField.placeholder);
    if (newField.confirmationLabel) newField.confirmationLabel = t(newField.confirmationLabel);
    if (newField.type === "daterange") {
      if (newField.startDateLabel) newField.startDateLabel = t(newField.startDateLabel);
      if (newField.endDateLabel) newField.endDateLabel = t(newField.endDateLabel);
    }
    delete newField.summaryValue;
    delete newField.fromStep;
    return newField;
  }, [field, t]);

  const setFieldErrors = (errors: string[]) => {
    field.fieldError = errors.length > 0;
    field.errors = errors;
    onFieldValidationChange?.();
  };

  const onValueChange = (
    value: string | number | boolean | ComplexInputValues | GooglePlaceAddress | null
  ) => {
    if (field.type === "autocomplete") {
      field.value = value;
      field.summaryValue = (value as GooglePlaceAddress | null)?.formatted_address;
      return;
    }
    if (field.type === "radio" || field.type === "radioshape") {
      field.value = value;
      field.summaryValue = t(
        `${field.options?.find((option) => option.value === value)?.text ?? ""}`
      );
      if (value) toNextStep();
      return;
    }
    if (value !== null && typeof value === "object" && "values" in value) {
      field.value =
        Object.keys(value.values).length === 1 ? Object.values(value.values)[0] : value.values;
      field.summaryValue = value.summaryValue;
    } else {
      field.value = value;
      if (field.type === "budget") {
        field.value = field.value as number;
        const currency = getCurrency();
        field.summaryValue = currency.template.replace("{val}", value);
        field.currency = currency.iso;
      } else {
        field.summaryValue = `${value}`;
      }
    }
  };

  if (Component) {
    return (
      <Component
        {...getTransformField}
        setFieldErrors={setFieldErrors}
        onValueChange={onValueChange}
        registerValidation={registerValidation}
      />
    );
  }
  return <div>The component {field.type} has not been created yet.</div>;
};
